import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Link from '@material-ui/core/Link';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import backgroundImg from './assets/background-new.png';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
  },
  row: {
    height: '100%',
    alignItems: 'center',
  },
  hwLogo: {
    width: '120px',
    height: '120px',
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: '99.6%',
    letterSpacing: '-0.04rem',
    fontSize: 24,
    margin: '10px 0px 15px 0px',
  },
  subtitle: {
    fontWeight: 'bold',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: 36,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 72,
    },
  },
  welcome: {
    marginBottom: '15px',
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 24,
    },
  },
  formWrapper: {
    width: '100%',
  },
  label: {
    color: theme.palette.secondary.main,
    letterSpacing: '-0.04em',
  },
  registerNext: {
    width: '100%',
    height: '60px',
    margin: '30px 0px 15px 0px',
    borderRadius: '4px',
    fontSize: 24,
    textTransform: 'none',
  },
  login: {
    fontSize: 18,
    lineHeight: '23px',
    letterSpacing: '-0.04em',
  },
}));

const { Group, Label, Control } = Form;

const PageWrapper = styled.div`
  background: url(${backgroundImg});
  background-size: cover;
  background-repeat: no-repeat;
  height: 100%;
`;

const Card = styled.div`
  background: #ffffff;
  width: 100%;
  margin: 0 auto;
  border-radius: 8px;
  height: auto;
  padding: 30px 45px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleWrapper = styled.div`
  color: #6d61ab;
  width: 100%;
`;

const Feedback = ({ invalidType }) => {
  const feedback =
    invalidType == 'password'
      ? 'Your password must be at least 8 characters.'
      : `Please provide a valid ${invalidType}.`;
  return (
    <>
      <Control.Feedback>Looks good!</Control.Feedback>
      <Control.Feedback type="invalid">{feedback}</Control.Feedback>
    </>
  );
};

const FailedRegistration = ({ openModal, setOpenModal, errorMessage }) => {
  const classes = useStyles();
  const history = useHistory();

  return (
    <Modal
      show={openModal}
      centered
      size="lg"
      aria-labelledby="contained-modal-title-vcenter">
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          {errorMessage
            ? errorMessage
            : 'Something went wrong. Please try again.'}
          <div>
            If you need assistance, please contact us at{' '}
            <Link href="mailto:hello@hackwestern.com">
              hello@hackwestern.com
            </Link>
            .
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Footer>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setOpenModal(false);
            history.push('/login');
          }}>
          Return to Login
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const RegisterPage = () => {
  const classes = useStyles();
  const history = useHistory();

  const [next, setNext] = useState(false);
  const [validated, setValidated] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [confirmPattern, setConfirmPattern] = useState(null);

  const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);
  const passwordConfirmationRef = useRef(null);

  const [user, setUser] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirmation: '',
  });

  const handleChange = (fieldName) => {
    switch (fieldName) {
      case 'firstName':
        setUser({
          ...user,
          firstname: firstNameRef.current.value,
        });
        break;
      case 'lastName':
        setUser({
          ...user,
          lastname: lastNameRef.current.value,
        });
        break;
      case 'email':
        setUser({
          ...user,
          email: emailRef.current.value,
        });
        break;
      case 'phone':
        setUser({
          ...user,
          phone: phoneRef.current.value,
        });
        break;
      case 'password':
        setUser({
          ...user,
          password: passwordRef.current.value,
        });
        break;
      case 'passwordConfirmation':
        let updatedPattern = passwordRef.current.value.replace('$', `\\$`);
        updatedPattern = updatedPattern.replace('^', `\\^`);
        updatedPattern = updatedPattern.replace('|', `\\|`);
        updatedPattern = updatedPattern.replace('?', `\\?`);
        updatedPattern = updatedPattern.replace('*', `\\*`);
        updatedPattern = updatedPattern.replace('+', `\\+`);
        setConfirmPattern(updatedPattern);
        setUser({
          ...user,
          passwordConfirmation: passwordConfirmationRef.current.value,
        });
        break;

      default:
        break;
    }
  };

  const registrationTitle =
    next && user.firstname && user.lastname
      ? `Welcome, ${user.firstname}`
      : 'Registration';

  return (
    <PageWrapper>
      <Container fluid className={classes.root}>
        <Row className={classes.row}>
          <Col xs={0} sm={0} md={1} lg={2} xl={2} />
          <Col>
            <Card>
              <FailedRegistration
                openModal={openModal}
                setOpenModal={setOpenModal}
                errorMessage={errorMessage}
              />
              <img
                src={require('./assets/hw-logo.png')}
                alt="hw-logo"
                className={classes.hwLogo}
              />
              <TitleWrapper>
                <p className={classes.title}>Hack Western 7</p>
                <p className={classes.subtitle}>{registrationTitle}</p>
                <p className={classes.welcome}>
                  {!next && 'Welcome, let’s get to know you.'}
                </p>
              </TitleWrapper>
              <Form
                className={classes.formWrapper}
                noValidate
                validated={validated}
                onSubmit={(event) => {
                  const form = event.currentTarget;
                  event.preventDefault();
                  event.stopPropagation();
                  setValidated(!form.checkValidity());

                  if (!next) {
                    setNext(user.firstname && user.lastname);
                  } else {
                    const link =
                      process.env.NODE_ENV === 'production'
                        ? process.env.REACT_APP_DROPLET_URL +
                          '/api/user/createUser'
                        : 'http://localhost:8080/api/user/createUser';
                    console.log(process.env.REACT_APP_ENVIRONMENT);
                    console.log(process.env.REACT_APP_DROPLET_URL);
                    console.log(process.env);

                    if (!form.checkValidity()) {
                      return;
                    }

                    axios
                      .post(
                        link,
                        {
                          ...user,
                          passwordConfirmation: window.btoa(
                            user.passwordConfirmation
                          ),
                          password: window.btoa(user.password),
                        },
                        {
                          withCredentials: true,
                        }
                      )
                      .then((response) => {
                        history.push('/login');
                      })
                      .catch((error) => {
                        if (error.response) {
                          if (error.response.status === 409) {
                            setErrorMessage(
                              'This email is being used. If it is yours, try resetting your password.'
                            );
                          }
                        }
                        setOpenModal(true);
                      });
                  }
                }}>
                {!next ? (
                  <Form.Row>
                    <Group as={Col} sm={12} md={6}>
                      <Label>
                        <Typography variant="h3" className={classes.label}>
                          First Name
                        </Typography>
                      </Label>
                      <Control
                        required
                        type="text"
                        placeholder="Ex. Elon"
                        ref={firstNameRef}
                        onChange={() => handleChange('firstName')}
                      />
                      <Feedback invalidType="First Name" />
                    </Group>
                    <Group as={Col} sm={12} md={6}>
                      <Label>
                        <Typography variant="h3" className={classes.label}>
                          Last Name
                        </Typography>
                      </Label>
                      <Control
                        required
                        type="text"
                        placeholder="Ex. Musk"
                        ref={lastNameRef}
                        onChange={() => handleChange('lastName')}
                      />
                      <Feedback invalidType="Last Name" />
                    </Group>
                  </Form.Row>
                ) : (
                  <>
                    <Form.Row>
                      <Group as={Col} sm={12} md={6}>
                        <Label>
                          <Typography variant="h3" className={classes.label}>
                            Email
                          </Typography>
                        </Label>
                        <Control
                          required
                          type="email"
                          placeholder="Ex. elon@teslaspacexboringcompany.com"
                          ref={emailRef}
                          onChange={() => handleChange('email')}
                        />
                        <Feedback invalidType="email address" />
                      </Group>
                      <Group as={Col} sm={12} md={6}>
                        <Label>
                          <Typography variant="h3" className={classes.label}>
                            Phone
                          </Typography>
                        </Label>
                        <Control
                          required
                          type="tel"
                          placeholder="Ex. 123-456-7890"
                          ref={phoneRef}
                          onChange={() => handleChange('phone')}
                        />
                        <Feedback invalidType="phone number" />
                      </Group>
                    </Form.Row>
                    <Form.Row>
                      <Group as={Col} sm={12} md={6}>
                        <Label>
                          <Typography variant="h3" className={classes.label}>
                            Password
                          </Typography>
                        </Label>
                        <Control
                          required
                          type="password"
                          minLength={8}
                          placeholder="Ex. securepassword"
                          ref={passwordRef}
                          onChange={() => handleChange('password')}
                        />
                        <Feedback invalidType="password" />
                      </Group>
                      <Group as={Col} sm={12} md={6}>
                        <Label>
                          <Typography variant="h3" className={classes.label}>
                            Re-Enter Password
                          </Typography>
                        </Label>
                        <Control
                          required
                          type="password"
                          placeholder="Ex. securepassword"
                          pattern={confirmPattern}
                          ref={passwordConfirmationRef}
                          onChange={() => handleChange('passwordConfirmation')}
                        />
                        {user.password !== user.passwordConfirmation ||
                        user.passwordConfirmation == '' ? (
                          <Control.Feedback type="invalid">
                            Please make sure your passwords match
                          </Control.Feedback>
                        ) : (
                          <Control.Feedback>Looks good!</Control.Feedback>
                        )}
                      </Group>
                    </Form.Row>
                  </>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  className={classes.registerNext}>
                  {next ? 'Register' : 'Next'}
                </Button>
              </Form>
              <p className={classes.login}>
                Already Registered? <Link href="/login">Login</Link>
              </p>
            </Card>
          </Col>
          <Col xs={0} sm={0} md={1} lg={2} xl={2} />
        </Row>
      </Container>
    </PageWrapper>
  );
};

export default RegisterPage;
